import { useState } from 'react'
import { useAppDispatch, useAppSelector } from 'store'
import {
  addGridFiltersByFilterId,
  pageableSettings,
  resetGridFilters,
  setGridFilters,
} from 'store/grid/grid.slice'
import { FilterDescriptor, GridFilterActionState, GridFilterState } from 'store/grid/types'

import { calculatePageIndex } from '../helpers'

const useMapStateToProps = () => {
  const gridState = useAppSelector((state) => state.grid)
  const hasFilters = gridState.filter.filters.length > 0

  const hasFilterName = !!gridState.filterName
  const [pageSize] = pageableSettings.pageSizes

  return {
    ...gridState,
    hasFilters,
    hasFilterName,
    pageIndex: gridState?.pageIndex ?? calculatePageIndex(gridState.skip, gridState.take),
    pageSize: gridState?.take ?? pageSize,
  }
}

export interface DispatchProps {
  setGridFilters: (filters: GridFilterActionState) => void
  addGridFiltersByFilterId: (filter: FilterDescriptor) => void
  resetGridFilters: () => void
}

export const useMapDispatchToProps = (): DispatchProps => {
  const dispatch = useAppDispatch()

  return {
    setGridFilters: (filters: GridFilterActionState) => dispatch(setGridFilters(filters)),
    addGridFiltersByFilterId: (filter: FilterDescriptor) =>
      dispatch(addGridFiltersByFilterId(filter)),
    resetGridFilters: () => dispatch(resetGridFilters()),
  }
}

export interface GridState extends GridFilterState {
  hasFilters: boolean
  hasFilterName: boolean
}

export const useGridState = (): {
  gridState: GridState
  gridActions: DispatchProps
} => {
  const state = useMapStateToProps()
  const action = useMapDispatchToProps()

  return {
    gridState: state,
    gridActions: action,
  }
}

export const useSingleGridState = (): {
  gridState: GridState
  gridActions: DispatchProps
} => {
  const [gridState, setGridState] = useState<GridState>({
    filter: {
      filters: [],
      logic: 'and',
    },
    sort: [],
    skip: 0,
    take: 10,
    total: 0,
    pageIndex: 1,
    pageSize: 10,
    hasFilters: false,
    hasFilterName: false,
    filterName: '',
    group: [],
  })

  const setGridFilters = (filters: GridFilterActionState) => {
    setGridState({
      ...gridState,
      ...filters,
    })
  }

  const addGridFiltersByFilterId = (filter: FilterDescriptor) => {
    setGridState({
      ...gridState,
      filter: {
        ...gridState.filter,
        filters: [...gridState.filter.filters, filter],
      },
    })
  }

  const resetGridFilters = () => {
    setGridState({
      filter: {
        filters: [],
        logic: 'and',
      },
      sort: [],
      skip: 0,
      take: 10,
      total: 0,
      pageIndex: 1,
      pageSize: 10,
      hasFilters: false,
      hasFilterName: false,
      filterName: '',
      group: [],
    })
  }

  return {
    gridState,
    gridActions: {
      setGridFilters,
      addGridFiltersByFilterId,
      resetGridFilters,
    },
  }
}
